import { TripFill30Icon } from "@vygruppen/spor-icon-react";
import {
  SharedContentGridLayout,
  SharedGridStyleBaseColor,
} from "features/CenterContent/shared/MainContentGrid";
import { AlternativeTransportLeftPanelScroll } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/AlternativeTransportLeftPanelScroll";
import { Itinerary } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/Itinerary";
import { SimpleHeadedWidget } from "features/CenterContent/VehicleDetails/shared/SimpleHeadedWidget";
import { VehiclePhoneNumber } from "features/CenterContent/VehicleDetails/shared/VehiclePhoneNumber";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Box, HStack, Text } from "shared/components/Spor";
import { AlternativeTransportVehicle } from "shared/types/alternativeTransport";
import {
  hideOnWidthClassname,
  hideOnWidthStyle,
} from "shared/utils/hideOnWidth";
import styled, { useTheme } from "styled-components";

export const breakpoint = 900;

const LeftContentGrid = styled.div`
  ${SharedContentGridLayout};
  grid-template-columns: 1fr 1fr;
  grid-template-rows: min(220px) 1fr;
  grid-template-areas:
    "bus-info bus-incidents"
    "bus-scroll bus-scroll";
  ${hideOnWidthStyle(breakpoint)};

  @container (width < 1850px) {
    grid-template-columns: 1fr;
    grid-template-rows: min(220px) 1fr;
    grid-template-areas:
      "bus-info"
      "bus-scroll";
  }
  padding-bottom: 50px;
`;

const LeftContentContainer = styled(Box)`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
  height: 100%;
  background-color: ${({ theme }) => theme.colorBackgroundMain};
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
`;
interface AlternativeTransportLeftPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const AlternativeTransportLeftPanel = ({
  vehicle,
}: AlternativeTransportLeftPanelProps) => {
  const theme = useTheme();

  const isCancelled = vehicle.vehicleStatus === "CANCELLED";

  const textDecorationStyle = {
    textDecoration: isCancelled ? "line-through" : "none",
  };

  const trainNumberTurnicate =
    vehicle.trainIds.join(", ").length > 16
      ? `${vehicle.trainIds.join(", ").substring(0, 16)}...`
      : vehicle.trainIds.join(", ");

  return (
    <LeftContentContainer>
      <HStack pb="6px" pt="12px" minW="450px" minH="16px">
        <TripFill30Icon />
        <Text fontWeight="700" fontSize="md" zIndex={1}>
          Avgang:{" "}
          {vehicle.workShiftPlan?.shiftId
            ? `${vehicle.workShiftPlan?.shiftId}-`
            : ""}
          {trainNumberTurnicate && !vehicle.workShiftPlan?.shiftId ? (
            <>
              {trainNumberTurnicate}{" "}
              <Text
                fontWeight="700"
                fontSize="md"
                zIndex={1}
                display="inline"
                color={theme.colorTextSecondary}
              >
                Operative
              </Text>
            </>
          ) : (
            trainNumberTurnicate
          )}
        </Text>
        {vehicle.isPartOfShuttlePlan && (
          <Text
            color={theme.colorTextSecondary}
            style={{ ...textDecorationStyle }}
            fontWeight="700"
            fontSize="md"
            zIndex={1}
          >
            Pendel
          </Text>
        )}
      </HStack>
      <LeftContentGrid>
        <SimpleHeadedWidget
          style={{ gridArea: "bus-info" }}
          panelStyle={SharedGridStyleBaseColor}
          headingLeft={<Text>Info</Text>}
          headingRight={
            <VehiclePhoneNumber
              phoneNumberUpdate={vehicle.driverPhoneNumber}
              workShift={vehicle.workShiftPlan}
              id={vehicle.id}
              operatorUpdate={{
                operatorName: vehicle.operatorName,
                operatorId: vehicle.operatorId,
              }}
            />
          }
        >
          <Itinerary alternativeTransport={vehicle} />
        </SimpleHeadedWidget>
        <SimpleHeadedWidget
          className={hideOnWidthClassname("hide-on-small", breakpoint)}
          style={{ gridArea: "bus-incidents" }}
          panelStyle={SharedGridStyleBaseColor}
          headingLeft={<Text>Hendelser</Text>}
        >
          {vehicle.notes.length > 0 ? (
            <div style={{ width: "100%", padding: "0.75rem" }}>
              <ExpandableInfoMessage semantics="info" title="Info fra Brudd">
                {vehicle.notes.map((note) => (
                  <Text key={note}>{note}</Text>
                ))}
              </ExpandableInfoMessage>
            </div>
          ) : (
            <NoDataMessage
              panelStyle={SharedGridStyleBaseColor}
              reason="Det finnes ikke hendelse denne bussen"
            />
          )}
        </SimpleHeadedWidget>
        <AlternativeTransportLeftPanelScroll vehicle={vehicle} />
      </LeftContentGrid>
    </LeftContentContainer>
  );
};

export default AlternativeTransportLeftPanel;
